import moment from 'moment';
import { aiGenerated } from '../util';

export class VgtRemoteManger<T> {
  private _dateFormat: string;
  private _extraFilter: string = '';
  private readonly loadingFunction: LoadingFunction<T>;
  private loadingFuture: Promise<void> | null = null;
  public parameters: VgtParameters = new VgtParameters();
  private pendingReload = false;
  public results: Results<T> = new Results<T>();
  public searchableProperties: SearchableProperty[] = [];
  private searchDelayTask: number = 0;
  private vgtFilter: string = '';

  get dateFormat(): string {
    return this._dateFormat;
  }

  set dateFormat(value: string) {
    this._dateFormat = value;
  }

  get extraFilter(): string {
    return this._extraFilter;
  }

  set extraFilter(value: string) {
    this._extraFilter = value;
    this.updateFilter();
    this.reload();
  }

  get isLoading(): boolean {
    return !!this.loadingFuture;
  }

  constructor(loadingFunction: LoadingFunction<T>, dateFormat: string, searchableProperties?: SearchableProperty[]) {
    this.loadingFunction = loadingFunction;
    this._dateFormat = dateFormat;
    if (searchableProperties !== undefined) {
      this.searchableProperties = searchableProperties;
    }
  }

  pageEvent(event: { currentPage: number, prevPage: number, currentPerPage: number, total: number }): void {
    // params.currentPage - current page that pagination is at
    // params.prevPage - previous page
    // params.currentPerPage - number of items per page
    // params.total - total number of items in the table
    // console.log('pageEvent', event);
    this.parameters.page = event.currentPage - 1;
    this.parameters.pageSize = event.currentPerPage;
    this.reload();
  }

  sortEvent(event: { field: string, type: 'asc' | 'desc' }[]): void {
    // params[0].sortType - ascending or descending
    // params[0].columnIndex - index of column being sorted
    // console.log('sortEvent', event);
    this.parameters.sort = event
      .filter(e => ['asc', 'desc'].includes(e.type))
      .filter(e => e.field)
      .map(e => e.field + ' ' + e.type)
      .join(', ');
    this.reload();
  }

  filterEvent(event): void {
    // params.columnFilters - filter values for each column in the following format:
    // {field1: 'filterTerm', field3: 'filterTerm2')
    // console.log('filterEvent', event);
    this.reload();
  }

  perPageEvent(event: { currentPage: number, currentPerPage: number, total: number }): void {
    // params.currentPage - current page that pagination is at
    // params.currentPerPage - number of items per page
    // params.total - total number of items in the table
    // console.log('perPageEvent', event);
    this.parameters.pageSize = event.currentPerPage;
    this.reload();
  }

  @aiGenerated
  searchEvent(event: { searchTerm: string, rowCount: number }): void {
    // params.searchTerm - term being searched for
    // params.rowCount - number of rows that match search
    // console.log('searchEvent', event);

    // fix page index first
    this.parameters.page = 0;

    // do search now
    const rawInput = event.searchTerm.trim();
    if (!rawInput.length) {
      this.vgtFilter = '';
    } else {
      // 1. Handle "AND" splitting
      // This regex splits on a literal '+' OR the word 'AND' (case-insensitive)
      const segments = rawInput.split(/\s*\+\s*|\s+AND\s+/i);

      const segmentFilters = segments.map(segment => {
        const trimmedSegment = segment.trim();

        // Check for "Starts With" pattern: "term"
        const isStartsWith = trimmedSegment.startsWith('"') && trimmedSegment.endsWith('"') && trimmedSegment.length > 2;

        // Clean the term: remove quotes if present, escape single quotes
        const term = (isStartsWith ? trimmedSegment.slice(1, -1) : trimmedSegment)
          .trim()
          .replaceAll(/'/g, '\'\'');

        const termAsNumber = term.match(/^[0-9]+(.[0-9]+)?$/g) ? parseFloat(term) : null;
        const termAsBoolean = term === 'true' ? true : (term === 'false' ? false : null);

        // Generate the OR group for this specific segment
        const orGroup = this.searchableProperties
          .map(e => {
            switch (e.type) {
              case 'string':
                const operator = isStartsWith ? 'startsWith' : 'contains';
                return `${e.field} ${operator} '${term}'`;
              case 'number':
                return termAsNumber !== null ? `${e.field} = ${termAsNumber}` : null;
              case 'boolean':
                return termAsBoolean !== null ? `${e.field} = ${termAsBoolean}` : null;
              case 'date':
                // Only treat as a year if it's a 4-digit number between 2000 and 2100
                const isValidYear = termAsNumber !== null && termAsNumber >= 2000 && termAsNumber <= 2100;
                if (isValidYear) {
                  return '(' + e.field + ' >= \'' + new Date(termAsNumber, 0, 1).toISOString() + '\''
                    + ' AND ' + e.field + ' < \'' + new Date(termAsNumber + 1, 0, 1).toISOString() + '\')';
                } else {
                  // If it's not a year, try parsing as a full date (e.g., "12/05/2024")
                  const date = moment(term, this.dateFormat);
                  if (!date.isValid()) {
                    return null;
                  }
                  return '(' + e.field + ' >= \'' + date.toISOString() + '\''
                    + ' AND ' + e.field + ' < \'' + date.add(1, 'day').toISOString() + '\')';
                }
              default:
                return null;
            }
          })
          .filter(e => !!e)
          .join(' OR ');

        return orGroup ? `(${orGroup})` : null;
      }).filter(group => !!group);

      // 2. Join the segments with AND
      this.vgtFilter = segmentFilters.join(' AND ');
    }
    this.updateFilter();

    clearTimeout(this.searchDelayTask);
    this.searchDelayTask = setTimeout(() => this.reload(), 800);
  }

  async reload(): Promise<void> {
    if (this.loadingFuture) {
      this.pendingReload = true;
      return;
    }

    this.loadingFuture = this.load();
    try {
      await this.loadingFuture;
    } catch (err) {
      console.log(err);
    }
    this.loadingFuture = null;

    //reload again if another reload was scheduled while this reload was in progress
    if (this.pendingReload) {
      this.pendingReload = false;
      await this.reload();
    }
  }

  initExtraFilter(filter: string): void {
    this._extraFilter = filter;
  }

  private async load(): Promise<void> {
    this.results = new Results<T>();
    this.results = await this.loadingFunction(this.parameters);
  }

  private updateFilter() {
    this.parameters.filter = [this.extraFilter, this.vgtFilter]
      .map(e => e.trim())
      .filter(e => !!e)
      .map(e => '( ' + e + ' )')
      .join(' AND ');
  }
}


export class VgtParameters {
  page: number = 0;
  pageSize: number = 10;
  filter: string = '';
  sort: string = '';
}

export class Results<T> {
  totalRows: number = 0;
  data: T[] = [];
}

export type LoadingFunction<T> = (params: VgtParameters) => Promise<Results<T>>;

export type SearchableProperty = {
  field: string;
  type: 'string' | 'number' | 'boolean' | 'date';
}
